import React, { useState, useEffect, useCallback } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import { Button, Form, NumberInput } from "carbon-components-react";
import { Redo32, Save32 } from "@carbon/icons-react";

import { thereWasAProblem } from "@eave/shared/utils/alerts";

import APIClient from "web_modules/APIClient";

interface TRIDCureResponse {
  calculated_trid_cure: string;
  trid_cure: string;
}

const styles = require("./styles.module.scss");

const tridCureDisplay = (tridCure, calculatedTRIDCure): React.ReactNode => {
  if (Number(tridCure) !== Number(calculatedTRIDCure)) {
    return (
      <p>
        TRID Cure: <s>${calculatedTRIDCure}</s> ${tridCure}
      </p>
    );
  }

  return <p>TRID Cure: ${calculatedTRIDCure}</p>;
};

const TRIDForm = ({ appId }: { appId: number }) => {
  const [submitting, setSubmitting] = useState(false);
  const [tridCure, setTRIDCure] = useState<string | null>(null);
  const [calculatedTRIDCure, setCalculatedTRIDCure] = useState<string | null>(null);
  const [unresolvedFeeTolerances, setUnresolvedFeeTolerances] = useState<boolean>(false);

  const loadTRIDCure = useCallback(async () => {
    try {
      const resp = await APIClient.get<TRIDCureResponse>(
        `/uw/loan_applications/${appId}/trid_cure`,
      );

      const { trid_cure, calculated_trid_cure } = resp;
      setTRIDCure(trid_cure);
      setCalculatedTRIDCure(calculated_trid_cure);
    } catch (e) {
      if (e?.meta?.error_detail && e.meta.error_detail.includes("is of unknown tolerance")) {
        setUnresolvedFeeTolerances(true);
      } else {
        thereWasAProblem("getting TRID override");
        throw e;
      }
    }
  }, [appId]);

  useEffect(() => {
    loadTRIDCure();
  }, [loadTRIDCure]);

  if (unresolvedFeeTolerances) {
    return (
      <>
        <h2>TRID Cure</h2>
        <p>Fee tolerances not resolved.</p>
      </>
    );
  }

  if (tridCure === null) {
    return (
      <>
        <h2>TRID Cure</h2>
        <p>Loading TRID cure...</p>
      </>
    );
  }

  return (
    <Formik
      validationSchema={Yup.object().shape({
        tridCure: Yup.number().min(0),
      })}
      initialValues={{ tridCure }}
      onSubmit={async values => {
        setSubmitting(true);
        try {
          const resp = await APIClient.post<TRIDCureResponse>(
            `/uw/loan_applications/${appId}/trid_cure`,
            {
              amount: values.tridCure,
            },
          );

          setTRIDCure(resp.trid_cure);
          setCalculatedTRIDCure(resp.calculated_trid_cure);
        } catch (e) {
          thereWasAProblem("setting TRID override");
          throw e;
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, errors, handleSubmit, handleBlur, handleChange, touched }) => {
        const { tridCure: tridCureInput } = values;

        return (
          <>
            <h2>TRID Cure</h2>
            {tridCureDisplay(tridCure, calculatedTRIDCure)}
            <Form
              onSubmit={e => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <div className={styles.description}>
                <NumberInput
                  disabled={submitting}
                  id="tridCure"
                  name="tridCure"
                  label="TRID Cure Override"
                  value={tridCureInput}
                  invalid={!!errors.tridCure && touched.tridCure}
                  invalidText={errors.tridCure}
                  onBlur={handleBlur}
                  onChange={(e, f, val) => {
                    // if you click the up or down button rather than entering
                    // number you get 2 different events that need to be
                    // resolved.
                    if (e.type === "click") {
                      setTRIDCure(val);
                      handleChange({
                        ...e,
                        target: {
                          ...e.target,
                          name: "tridCure",
                          value: val,
                        },
                      });
                    } else {
                      setTRIDCure(e.target.value);
                      handleChange(e);
                    }
                  }}
                />
              </div>
              <div className={styles.flex}>
                <Button
                  kind="secondary"
                  type="button"
                  renderIcon={Redo32}
                  disabled={submitting}
                  onClick={async () => {
                    setSubmitting(true);

                    try {
                      const resp = await APIClient.post<TRIDCureResponse>(
                        `/uw/loan_applications/${appId}/trid_cure`,
                        {
                          amount: null,
                        },
                      );

                      setTRIDCure(resp.trid_cure);
                      setCalculatedTRIDCure(resp.calculated_trid_cure);
                    } catch (e) {
                      thereWasAProblem("setting TRID override");
                      throw e;
                    } finally {
                      setSubmitting(false);
                    }
                    handleChange({
                      target: {
                        name: "tridCure",
                        value: "0",
                      },
                    });
                  }}
                >
                  Remove TRID Cure Override
                </Button>
                <Button kind="primary" type="submit" renderIcon={Save32} disabled={submitting}>
                  Override TRID Cure
                </Button>
              </div>
            </Form>
          </>
        );
      }}
    </Formik>
  );
};

export default TRIDForm;
